import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { toast } from '../../utils/toast';
import type { Employee, Device } from '../../types';

interface EmployeeAssignmentModalProps {
  isOpen: boolean;
  groupId: string;
  onClose: () => void;
  onEmployeeAssigned: () => void;
}

export function DeviceAssignmentModal({
  isOpen,
  groupId,
  onClose,
  onEmployeeAssigned,
}: EmployeeAssignmentModalProps) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<Set<string>>(new Set());
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedEmployeeIds(new Set());
      setError(null);
      setSearchTerm('');
      fetchAvailableEmployees();
      fetchGroupDevices();
    }
  }, [isOpen, groupId]);

  const fetchAvailableEmployees = async () => {
    setFetchingEmployees(true);
    try {
      const response = await api.getAllEmployees({ limit: 1000 });
      setEmployees(response.employees || []);
    } catch (err: any) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
    } finally {
      setFetchingEmployees(false);
    }
  };

  const fetchGroupDevices = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/groups/${groupId}/devices`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const devices = data.data || [];
        
        // Extract unique employee IDs from devices
        const employeeIds = new Set<string>();
        devices.forEach((device: Device) => {
          if (device.idEmployee) {
            employeeIds.add(device.idEmployee);
          }
        });
        setAssignedEmployeeIds(employeeIds);
      }
    } catch (err: any) {
      console.error('Error fetching group devices:', err);
    }
  };

  const handleSelectEmployee = (employeeId: string) => {
    // Don't allow selecting already assigned employees
    if (assignedEmployeeIds.has(employeeId)) {
      return;
    }

    const newSelected = new Set(selectedEmployeeIds);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployeeIds(newSelected);
  };

  const handleSelectAll = () => {
    // Only select unassigned employees
    const unassignedEmployees = employees.filter(emp => !assignedEmployeeIds.has(emp.idEmployee));
    
    if (selectedEmployeeIds.size === unassignedEmployees.length) {
      setSelectedEmployeeIds(new Set());
    } else {
      setSelectedEmployeeIds(new Set(unassignedEmployees.map(emp => emp.idEmployee)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (selectedEmployeeIds.size === 0) {
      setError('Please select at least one employee');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/groups/${groupId}/assign-employees`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({ employee_ids: Array.from(selectedEmployeeIds) }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign employees');
      }

      toast.success(`${selectedEmployeeIds.size} employee(s) assigned successfully`);
      setSelectedEmployeeIds(new Set());
      onEmployeeAssigned();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to assign employees');
      toast.error(err.message || 'Failed to assign employees');
    } finally {
      setLoading(false);
    }
  };

  const unassignedEmployees = employees.filter(emp => !assignedEmployeeIds.has(emp.idEmployee));

  // Filter employees based on search term
  const filteredEmployees = unassignedEmployees.filter(emp => {
    const search = searchTerm.toLowerCase();
    return (
      (emp.NIP && emp.NIP.toLowerCase().includes(search)) ||
      emp.Nama.toLowerCase().includes(search) ||
      (emp.Jabatan && emp.Jabatan.toLowerCase().includes(search))
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-4">Assign Employees to Group</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Select Employees * ({selectedEmployeeIds.size} selected)
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {selectedEmployeeIds.size === unassignedEmployees.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by name, NIP, or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <p className="text-xs text-gray-500 mt-1">
                  Found {filteredEmployees.length} employee(s)
                </p>
              )}
            </div>

            {fetchingEmployees ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Loading employees...</p>
              </div>
            ) : employees.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No employees available.</p>
            ) : filteredEmployees.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No employees found matching "{searchTerm}"</p>
            ) : (
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  {filteredEmployees.map((employee) => {
                    const isAssigned = assignedEmployeeIds.has(employee.idEmployee);
                    const isSelected = selectedEmployeeIds.has(employee.idEmployee);
                    
                    return (
                      <div
                        key={employee.idEmployee}
                        className={`flex items-center p-3 border-b border-gray-200 last:border-b-0 ${
                          isAssigned ? 'bg-gray-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`employee-${employee.idEmployee}`}
                          checked={isSelected}
                          onChange={() => handleSelectEmployee(employee.idEmployee)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          disabled={loading || isAssigned}
                        />
                        <label
                          htmlFor={`employee-${employee.idEmployee}`}
                          className={`ml-3 flex-1 ${isAssigned ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{employee.Nama}</span>
                            {isAssigned && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Already Assigned
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {employee.NIP && `NIP: ${employee.NIP}`}
                            {employee.Jabatan && ` • ${employee.Jabatan}`}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading || selectedEmployeeIds.size === 0 || fetchingEmployees}
            >
              {loading ? 'Assigning...' : `Assign (${selectedEmployeeIds.size})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
